import type { BoxProps } from '@mui/material/Box';

import { m } from 'framer-motion';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { FormDivider } from 'src/auth/components/form-divider';

// ----------------------------------------------------------------------

export function Betingelser({ sx, ...other }: BoxProps) {
  return (
    <Box
      component="section"
      sx={[{ overflow: 'hidden' }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      <Container sx={{ py: { xs: 10, md: 15 } }}>
        <Grid container columnSpacing={{ md: 3 }} sx={{ alignItems: 'flex-start' }}>
          <Grid>
            <Typography component={m.h2} variant="h2" sx={{ mb: 3 }}>
              Vilkår og betingelser
            </Typography>

            <Typography
              component={m.p}
              sx={[
                (theme) => ({
                  color: 'text.secondary',
                  ...theme.applyStyles('dark', {
                    color: 'common.white',
                  }),
                }),
              ]}
            >
              Hos Melfar Serviceudlejning ApS bestræber vi os på at levere en service i høj kvalitet, og
              det er vigtigt for os, at der er klar forventningsafstemning mellem kunden og os. Vi
              anbefaler derfor, at nedenstående betingelser læses grundigt, før de accepteres. Ved
              at acceptere disse vilkår forpligter kunden sig til at overholde alle punkter uden
              forbehold.
            </Typography>
            <br />

            <h2>1. Generelt</h2>
            <ul>
              <li>
                <h4>1.1 Betaling</h4>
                <p>
                  Betaling skal ske umiddelbart efter arrangementets afslutning, medmindre andet er
                  aftalt. Som udgangspunkt modtager vi kontant betaling. Er dette ikke muligt, skal
                  beløbet overføres senest tre dage efter arrangementet til det kontonummer, der
                  fremgår af den fremsendte faktura.
                </p>
              </li>
              <li>
                <h4>1.2 Afbestilling og ændring af ordre</h4>
                <p>
                  Afbestilling eller nedgradering af en ordre kan ske op til syv dage før den
                  aftalte leveringsdato. Sker dette senere, forbeholder Melfar Serviceudlejning ApS sig
                  retten til at opkræve 50 % af prisen på de afbestilte eller nedgraderede produkter
                  som kompensation for tabt arbejdsfortjeneste. Opgradering af en ordre er altid
                  muligt, så længe lager haves.
                </p>
              </li>
              <li>
                <h4>1.3 Rengøring</h4>
                <p>
                  Alle produkter leveres i rengjort og kontrolleret stand og skal returneres i samme
                  stand. Eventuel rengøring ved returnering vil blive faktureret kunden.
                </p>
              </li>
              <li>
                <h4>1.4 Skader og erstatningsansvar</h4>
                <p>
                  Skader forvoldt af kunden, kundens gæster eller ubudne gæster på vores udstyr,
                  herunder under transport, skal erstattes af kunden. Omkostninger forbundet med
                  reparation eller udskiftning faktureres særskilt.
                </p>
              </li>
              <li>
                <h4>1.5 Lejeperiode</h4>
                <p>Lejeperioden er som udgangspunkt tre dage og består af:</p>

                <ul>
                  <li>Dag 1: Udlevering</li>
                  <li>Dag 2: Lejedag</li>
                  <li>Dag 3: Returnering</li>
                </ul>
                <p>
                  Af logistiske årsager kan leveringen ske tidligere og afhentning senere. Dette
                  aftales individuelt. Ønskes lejeperioden forlænget, kan dette ske mod betaling.
                </p>
              </li>
              <li>
                <h4>1.6 Levering og afhentning</h4>
                <p>
                  Levering sker til nærmeste overdækkede område efter forudgående aftale. Ved afhentning skal produkterne være pakket og klar samme sted, som de blev leveret – medmindre andet er aftalt.
                  Kunden er forpligtet til ved levering eller afhentning at kontrollere, at produkterne svarer til det bestilte. Eventuelle mangler eller fejl skal meddeles leverandøren samme dag. Såfremt der ved returnering konstateres manglende produkter, forbeholder Melfar Serviceudlejning sig ret til at kræve erstatning herfor.
                </p>
              </li>
              <li>
                <h4>1.7 Ubrugte produkter</h4>
                <p>
                  Der ydes ingen refundering eller kompensation for lejede produkter, der ikke er
                  blevet anvendt.
                </p>
              </li>
              <li>
                <h4>1.8 Forsinket returnering</h4>
                <p>
                  Ved forsinket returnering pålægges et gebyr på 25 % af den samlede ordre pr.
                  påbegyndt døgn.
                </p>
              </li>
            </ul>
            <br />
            <FormDivider label="" />

            <h2>2. Teltudlejning</h2>
            <ul>
              <li>
                <h4>2.1 Aflysning grundet vejrforhold</h4>
                <p>
                  Melfar Serviceudlejning ApS forbeholder sig retten til at aflyse en teltreservation,
                  hvis der varsles vejrforhold, som gør opsætningen uforsvarlig.
                </p>
              </li>
              <li>
                <h4>2.2 Spor i terrænet</h4>
                <p>
                  Ved leje af telt og/eller gulv kan der forekomme mærker på eller under terrænet.
                  Melfar Serviceudlejning ApS fraskriver sig ethvert ansvar for omkostninger relateret
                  hertil.
                </p>
              </li>
              <li>
                <h4>2.3 Opsætningsansvar</h4>
                <p>
                  Alle telte sikres med pløkker og/eller stormsikringer. Kunden er ansvarlig for at
                  sikre, at der ikke findes kabler, rør eller lignende i jorden, hvor teltet skal
                  opstilles. Eventuelle skader i denne forbindelse er Melfar Serviceudlejning ApS
                  uvedkommende.
                </p>
              </li>
              <li>
                <h4>2.4 Klargøring af område</h4>
                <p>
                  Området, hvor teltet skal opstilles, samt det umiddelbart nærliggende område, skal
                  være ryddet forud for det aftalte tidspunkt for opsætning og nedtagning.
                </p>
              </li>
            </ul>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
